import { motion } from "framer-motion";
import { Book, Calendar, Users, Trophy, Music, Palette, Heart, MapPin, Clock, Phone, Mail } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const mediathequeInfo = {
  title: "Médiathèque Municipale",
  description: "Un espace culturel moderne au cœur de Gréasque, offrant une large collection de livres, magazines, CD et DVD pour tous les âges.",
  hours: [
    { day: "Mardi", time: "14h00 - 18h00" },
    { day: "Mercredi", time: "10h00 - 12h00 / 14h00 - 18h00" },
    { day: "Vendredi", time: "14h00 - 18h00" },
    { day: "Samedi", time: "10h00 - 12h00 / 14h00 - 17h00" }
  ],
  services: [
    "Prêt de livres, magazines, CD et DVD",
    "Espace jeunesse avec animations",
    "Accès internet et wifi gratuit",
    "Ateliers et rencontres littéraires",
    "Catalogue en ligne consultable"
  ],
  contact: {
    phone: "04 42 69 86 06",
    email: "mediatheque@ville-greasque.fr"
  }
};

const centreCulturel = {
  title: "Centre Culturel",
  description: "Programmation culturelle riche et variée tout au long de l'année : spectacles, concerts, expositions et ateliers pour tous.",
  programmation: [
    {
      title: "Saison Théâtrale 2024-2025",
      description: "Une sélection de pièces contemporaines et classiques",
      icon: Music
    },
    {
      title: "Expositions d'Art",
      description: "Artistes locaux et régionaux mis à l'honneur",
      icon: Palette
    },
    {
      title: "Concerts et Spectacles",
      description: "Musique, danse et performances tout au long de l'année",
      icon: Music
    }
  ]
};

const associations = [
  {
    category: "Sport",
    icon: Trophy,
    color: "bg-primary/10 text-primary",
    items: [
      { name: "Football Club Gréasque", description: "École de foot et équipes seniors" },
      { name: "Tennis Club", description: "Cours et compétitions pour tous niveaux" },
      { name: "Yoga et Bien-être", description: "Ateliers hebdomadaires de yoga et relaxation" },
      { name: "Escalade", description: "Initiation et perfectionnement en salle" }
    ]
  },
  {
    category: "Culture",
    icon: Book,
    color: "bg-accent/10 text-accent",
    items: [
      { name: "Amis de la Médiathèque", description: "Promotion de la lecture et animations" },
      { name: "Atelier Théâtre", description: "Cours et représentations annuelles" },
      { name: "Chorale Municipale", description: "Répétitions et concerts" },
      { name: "Arts Plastiques", description: "Peinture, sculpture et expositions" }
    ]
  },
  {
    category: "Solidarité",
    icon: Heart,
    color: "bg-destructive/10 text-destructive",
    items: [
      { name: "Secours Populaire", description: "Aide alimentaire et vestimentaire" },
      { name: "Croix-Rouge", description: "Premiers secours et actions humanitaires" },
      { name: "Club des Aînés", description: "Activités et sorties pour seniors" },
      { name: "CCAS", description: "Centre Communal d'Action Sociale" }
    ]
  }
];

const equipements = [
  {
    name: "Stade Municipal",
    description: "Terrain de football, piste d'athlétisme, vestiaires",
    image: IMAGES.PLACE_5
  },
  {
    name: "Gymnase",
    description: "Salle multisports, équipements modernes",
    image: IMAGES.EVENEMENT_4
  },
  {
    name: "Courts de Tennis",
    description: "3 courts extérieurs éclairés",
    image: IMAGES.CULTURE_4
  }
];

export default function CultureLoisirs() {
  return (
    <div className="min-h-screen bg-background">
      <motion.div
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.CULTURE_4}
              alt="Culture et Loisirs"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div variants={fadeInUp} className="max-w-3xl">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                Culture & Loisirs
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-foreground">
                Vivre et S'épanouir à Gréasque
              </h1>
              <p className="text-xl text-muted-foreground">
                Découvrez notre riche offre culturelle et sportive : médiathèque, centre culturel, associations et équipements pour tous les âges.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">Médiathèque Municipale</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                {mediathequeInfo.description}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div variants={fadeInUp}>
                <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle>Horaires d'Ouverture</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {mediathequeInfo.hours.map((schedule, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                        <span className="font-medium text-foreground">{schedule.day}</span>
                        <span className="text-muted-foreground">{schedule.time}</span>
                      </div>
                    ))}
                    <div className="pt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-primary" />
                        <span className="text-muted-foreground">{mediathequeInfo.contact.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="text-muted-foreground">{mediathequeInfo.contact.email}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Book className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle>Services Proposés</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {mediathequeInfo.services.map((service, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                          <span className="text-muted-foreground">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">{centreCulturel.title}</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                {centreCulturel.description}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {centreCulturel.programmation.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div key={index} variants={fadeInUp}>
                    <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-all hover:scale-[1.02]">
                      <CardHeader>
                        <div className="p-4 rounded-lg bg-accent/10 w-fit mb-4">
                          <Icon className="w-8 h-8 text-accent" />
                        </div>
                        <CardTitle className="text-xl">{item.title}</CardTitle>
                        <CardDescription>{item.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">Associations</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Une vie associative dynamique au service de tous les Gréasquais
              </p>
            </motion.div>

            <div className="space-y-8">
              {associations.map((category, catIndex) => {
                const Icon = category.icon;
                return (
                  <motion.div key={catIndex} variants={fadeInUp}>
                    <Card className="border-border/50 shadow-sm">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-lg ${category.color}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <CardTitle className="text-2xl">{category.category}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          {category.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                              <h4 className="font-semibold text-foreground mb-2">{item.name}</h4>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">Équipements Sportifs</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Des infrastructures modernes pour la pratique sportive
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {equipements.map((equipement, index) => (
                <motion.div key={index} variants={fadeInUp}>
                  <Card className="overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-all hover:scale-[1.02]">
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={equipement.image}
                        alt={equipement.name}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl">{equipement.name}</CardTitle>
                      <CardDescription>{equipement.description}</CardDescription>
                    </CardHeader>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp} className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6 text-foreground">Rejoignez-nous !</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Que vous soyez passionné de sport, de culture ou de solidarité, il y a une place pour vous dans la vie associative gréasquaise.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button size="lg" className="bg-primary hover:bg-primary/90">
                  <Users className="w-5 h-5 mr-2" />
                  Découvrir les Associations
                </Button>
                <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10">
                  <Calendar className="w-5 h-5 mr-2" />
                  Voir l'Agenda
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </div>
  );
}